import { ClearRounded } from "@mui/icons-material";
import { Box, Button, CircularProgress, Dialog, DialogActions, DialogTitle, IconButton, Tooltip } from "@mui/material";
import { grey } from "@mui/material/colors";
import axios from "axios";
import SelectInputField from "components/EboStoreV4/CommonComponents/SelectInputField/SelectInputField";
import TextInputField from "components/EboStoreV4/CommonComponents/TextInputField/TextInputField";
import { GlobalState } from "context/GlobalContext";
import { attributeValueAssociationName, constraints, inputTypes } from "data/pimData";
import { useEffect, useMemo, useState } from "react";

const RenderInputFields = ({ columns, setData, data, width }) => {
	return (
		<Box display="flex" flexWrap="wrap" p={0} alignItems="center" columnGap="2%" rowGap="12px" width="100%">
			{Object.keys(columns).map((columnName) => {
				const column = columns[columnName];
				const value = data?.[column.key];

				const tooltipTitle = value !== undefined && value !== null && value !== "" ? String(value) : "No value";

				/* -------- TEXT INPUT -------- */
				if (column.input_type === inputTypes.enter || column.input_type === inputTypes.autoGenerate) {
					return (
						<Tooltip key={column.key} title={tooltipTitle} arrow placement="top">
							<span
								style={{
									width: "32%",
									display: "inline-block",
								}}>
								<TextInputField
									sx={{
										width: "100%",
										p: 1,
										background: grey[200],
										fontWeight: 400,
										overflow: "hidden",
										boxSizing: "border-box",
									}}
									data={data}
									setData={setData}
									column={column}
									disabled={column.input_type === inputTypes.autoGenerate}
									required={column.type?.[constraints.notNull]}
									type={column.type?.datatype}
								/>
							</span>
						</Tooltip>
					);
				}

				/* -------- SELECT INPUT -------- */
				return (
					<Tooltip key={column.key} title={tooltipTitle} arrow placement="top">
						<span
							style={{
								width: "32%",
								display: "inline-block",
							}}>
							<SelectInputField
								sx={{
									width: "100%",
									p: 1,
									background: grey[200],
									fontWeight: 400,
									overflow: "hidden",
								}}
								data={data}
								setData={setData}
								column={column}
								width="100%"
								options={column?.[attributeValueAssociationName]}
								required={column.type?.[constraints.notNull]}
							/>
						</span>
					</Tooltip>
				);
			})}
		</Box>
	);
};

// Edit Model
const EditModal = ({ modalOpen, setModalOpen, selectedStore, columns }) => {
	const handleCloseModal = () => {
		setModalOpen(false);
	};

	const { setSnackBar, snackBar, setSnackBarOpen } = GlobalState();

	const [selectedStoreData, setSelectedStoreData] = useState({});
	const [originalData, setOriginalData] = useState({});
	const [loading, setLoading] = useState(false);

	useEffect(() => {
		if (selectedStore) {
			const copy = { ...selectedStore };
			setSelectedStoreData(copy);
			setOriginalData(copy);
		}
	}, [selectedStore]);

	const isDirty = useMemo(() => {
		return JSON.stringify(selectedStoreData) !== JSON.stringify(originalData);
	}, [selectedStoreData, originalData]);

	const handleUpdate = async () => {
		if (!selectedStoreData?.site_code) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Missing store identifier please try with upload option",
				severity: "warning",
			});
			return;
		}

		// Build update payload (exclude auto-generated fields)
		const updates = {};
		Object.keys(columns).forEach((col) => {
			const column = columns[col];
			const key = column.key;

			if (column.input_type !== inputTypes.autoGenerate && selectedStoreData[key] !== originalData[key]) {
				updates[key] = selectedStoreData[key];
			}
		});

		if (Object.keys(updates).length === 0) {
			handleCloseModal();
			return;
		}

		const payload = {
			site_code: selectedStoreData.site_code, // WHERE condition
			updates,
		};

		try {
			setLoading(true);

			await axios.put("/api/pos_reco/update_store_data", payload);

			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Store updated successfully",
				severity: "success",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});

			handleCloseModal();
		} catch (error) {
			setSnackBarOpen(true);
			setSnackBar({
				...snackBar,
				message: "Unable to update store",
				severity: "error",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
		} finally {
			setLoading(false);
		}
	};

	return (
		<Dialog open={modalOpen} onClose={handleCloseModal} scroll="body" maxWidth="md" fullWidth>
			<Box display="flex" justifyContent="space-between" p="5px 10px" alignItems="center" component="form">
				<DialogTitle>Edit Store</DialogTitle>

				<IconButton sx={{ width: 50, height: 50, marginLeft: "auto" }} onClick={handleCloseModal}>
					<ClearRounded sx={{ transform: "scale(1.3)" }} />
				</IconButton>
			</Box>

			<Box p={2}>
				<RenderInputFields data={selectedStoreData} setData={setSelectedStoreData} columns={columns} />

				<DialogActions sx={{ paddingTop: 2, alignItems: "center" }}>
					<Button variant="outlined" onClick={handleCloseModal}>
						Cancel
					</Button>

					<Button
						variant="contained"
						onClick={handleUpdate}
						startIcon={loading ? <CircularProgress color="inherit" size={20} /> : null}
						disabled={!isDirty || loading}>
						{loading ? "Saving" : "Save"}
					</Button>
				</DialogActions>
			</Box>
		</Dialog>
	);
};

export default EditModal;
